import { supabase } from './supabase';

/**
 * Coach → player shared feedback (Phase 5, migration 00089).
 *
 * A coach explicitly shares a development note with a player from a session
 * review. Both helpers degrade gracefully if 00089 hasn't been applied yet
 * (the insert/select error is swallowed) so the app keeps working pre-migration.
 */

export interface SharedNote {
  id: string;
  player_id: string;
  note: string;
  session_id: string | null;
  created_at: string;
}

export interface SharePlayerNoteInput {
  sessionId: string | null;
  teamId: string;
  playerId: string;
  coachId: string;
  note: string;
}

/** Share a coach note with a player — the explicit opt-in (consent) action.
 *  Returns { ok:false } with a message on any failure (incl. unmigrated table). */
export async function sharePlayerNote(input: SharePlayerNoteInput): Promise<{ ok: boolean; error?: string }> {
  if (!input.note.trim()) return { ok: false, error: 'Write a note first.' };
  try {
    const { error } = await supabase.from('session_player_notes').insert({
      session_id: input.sessionId,
      team_id: input.teamId,
      player_id: input.playerId,
      coach_id: input.coachId,
      note: input.note.trim(),
      share_with_player: true,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** Notes shared with a player. Coach sees their team's via RLS; the player
 *  sees their own shared notes. Returns [] on any error (incl. unmigrated). */
export async function listSharedNotesForPlayer(playerId: string): Promise<SharedNote[]> {
  try {
    const { data, error } = await supabase
      .from('session_player_notes')
      .select('id, player_id, note, session_id, created_at')
      .eq('player_id', playerId)
      .eq('share_with_player', true)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error || !data) return [];
    return data as SharedNote[];
  } catch {
    return [];
  }
}
